<template>
    <section class="feature-group-section bg-gray-50">
        <landing-header />

        <div class="max-w-7xl mx-auto px-4 py-32">

            <div class="text-center mb-16">
                <h2 class="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
                    Combien perdez-vous <br />
                    <span class="gradient-text">chaque mois sans Termoflow ?</span>
                </h2>
                <p class="text-xl text-gray-600 max-w-3xl mx-auto">
                    Avec une estimation de 1 heure productive gagnée par jour et par technicien.
                </p>
            </div>

            <div class="bg-white rounded-3xl shadow-xl border border-gray-100 p-10">

                <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">

                    <div>
                        <label class="text-sm font-bold text-gray-700">Nombre de techniciens</label>
                        <input type="number" min="0" v-model.number="techs" class="w-full mt-2 border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-orange-400 focus:outline-none" />
                    </div>

                    <div>
                        <label class="text-sm font-bold text-gray-700">Taux horaire facturé (€)</label>
                        <input type="number" min="0" v-model.number="hourRate" class="w-full mt-2 border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-orange-400 focus:outline-none" />
                    </div>

                    <div>
                        <label class="text-sm font-bold text-gray-700">Jours travaillés / mois</label>
                        <input type="number" min="0" v-model.number="workingDays" class="w-full mt-2 border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-orange-400 focus:outline-none" />
                    </div>

                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">

                    <div>
                        <p class="text-sm text-gray-500 mb-2">Gain mensuel estimé</p>
                        <p class="text-4xl font-extrabold text-green-600">
                            {{ formatCurrency(monthlyGain) }}
                        </p>
                    </div>

                    <div>
                        <p class="text-sm text-gray-500 mb-2">Gain annuel estimé</p>
                        <p class="text-4xl font-extrabold text-gray-900">
                            {{ formatCurrency(yearlyGain) }}
                        </p>
                    </div>

                    <div>
                        <p class="text-sm text-gray-500 mb-2">ROI mensuel</p>
                        <p class="text-4xl font-extrabold text-orange-600">
                            {{ roiPercentage.toFixed(0) }}%
                        </p>
                    </div>

                </div>

                <div class="mt-16 border-t pt-12 text-center">

                    <p class="text-lg text-gray-600 mb-2">
                        Coût mensuel des licences (55€ / technicien)
                    </p>

                    <p class="text-2xl font-bold text-gray-900 mb-8">
                        {{ formatCurrency(totalLicenseCost) }}
                    </p>

                    <p class="text-lg text-gray-600 mb-2">
                        Bénéfice net estimé
                    </p>

                    <p class="text-5xl font-black gradient-text">
                        {{ formatCurrency(netMonthlyProfit) }} / mois
                    </p>

                    <p class="mt-6 text-gray-500">
                        Une seule journée d’intervention suffit à amortir la licence.
                    </p>

                </div>

                <div class="mt-20 max-w-5xl mx-auto">

                    <div class="text-center mb-12">
                        <h3 class="text-3xl font-extrabold text-gray-900 mb-4">
                            Pourquoi ce gain est réaliste ?
                        </h3>
                        <p class="text-gray-600">
                            Termoflow ne crée pas du travail. Il supprime les frictions et augmente la valeur de chaque intervention.
                        </p>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

                        <div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <h4 class="font-bold text-gray-900 mb-4">1. Moins d’administratif</h4>
                            <p class="text-gray-600">
                                CERFA pré-remplis, données centralisées, moins de retours bureau.
                                Chaque minute récupérée devient facturable.
                            </p>
                        </div>

                        <div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <h4 class="font-bold text-gray-900 mb-4">2. Moins d’erreurs réglementaires</h4>
                            <p class="text-gray-600">
                                Registre fluides automatique, suivi bouteilles et alertes étalonnage.
                                Moins de risques, moins de reprises.
                            </p>
                        </div>

                        <div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <h4 class="font-bold text-gray-900 mb-4">3. Plus d’interventions par jour</h4>
                            <p class="text-gray-600">
                                Une heure gagnée, c’est une intervention en plus.
                                Sur un mois, l’impact est significatif.
                            </p>
                        </div>

                        <div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <h4 class="font-bold text-gray-900 mb-4">4. Meilleure organisation</h4>
                            <p class="text-gray-600">
                                Planning optimisé, vision globale, décisions pilotées par les chiffres.
                                Moins d’improvisation, plus de marge.
                            </p>
                        </div>

                        <div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <h4 class="font-bold text-gray-900 mb-4">5. Image professionnelle renforcée</h4>
                            <p class="text-gray-600">
                                Portail client, SMS de rappel avant visite, accès aux documents en ligne.
                                Moins d’annulations de dernière minute, plus de confiance et de recommandations.
                            </p>
                        </div>

                        <div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <h4 class="font-bold text-gray-900 mb-4">6. Récurrence sécurisée</h4>
                            <p class="text-gray-600">
                                Relances automatiques des entretiens annuels.
                                Vous ne perdez plus de contrats par oubli.
                                Votre chiffre d’affaires devient prévisible.
                            </p>
                        </div>

                    </div>

                </div>



            </div>

        </div>
    </section>
</template>

<script>
import LandingHeader from './LandingHeader.vue'

export default {
    name: 'LandingROI',
    components: {
        LandingHeader
    },
    data() {
        return {
            techs: 1,
            hourRate: 60,
            workingDays: 20,
            licensePrice: 55
        }
    },
    computed: {
        hoursSavedPerDay() {
            return 1
        },
        monthlyGain() {
            return this.techs * this.hourRate * this.workingDays * this.hoursSavedPerDay
        },
        yearlyGain() {
            return this.monthlyGain * 12
        },
        totalLicenseCost() {
            return this.techs * this.licensePrice
        },
        netMonthlyProfit() {
            return this.monthlyGain - this.totalLicenseCost
        },
        roiPercentage() {
            if (this.totalLicenseCost === 0) return 0
            return (this.netMonthlyProfit / this.totalLicenseCost) * 100
        }
    },
    methods: {
        formatCurrency(value) {
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value)
        }
    }
}
</script>
